import { fetchApi, ApiError } from "./client";

export interface ReferralSummary {
  referralCode: string | null;
  counts: {
    registered: number;
    pending: number;
    rewarded: number;
  };
  recentReferrals: {
    id: string;
    status: string;
    appliedAt: string;
    qualifiedAt: string | null;
    refereeName: string;
    refereeAvatar: string | null;
  }[];
  /** The code this user was referred with. Non-null once set, and permanent. */
  referredBy: {
    code: string | null;
    name: string;
    status: string;
    appliedAt: string;
  } | null;
  /** False once the user has been referred or has made their first paid purchase. */
  canApplyCode: boolean;
}

export interface ReferralReward {
  id: string;
  rewardTarget: "REFERRER" | "REFEREE";
  status: "PENDING" | "ISSUED" | "REDEEMED" | "EXPIRED" | "REVOKED" | string;
  issuedAt: string | null;
  expiresAt: string | null;
  redeemedAt: string | null;
  revokedAt: string | null;
  revocationReason: string | null;
  rewardConfig: {
    discountType: "FIXED" | "PERCENTAGE" | string;
    discountValue: number;
    currency: string;
  } | null;
  /** Redeemable voucher code — non-null only when status === "ISSUED" */
  voucherCode: string | null;
}

/**
 * HTTP statuses `POST /referrals/apply` returns for a definitive,
 * non-retryable rejection: bad code (404), self-referral (400),
 * already-referred (409), apply-window-closed (403). Any other failure
 * (network error, 5xx, timeout) is transient — the caller should keep the
 * pending code around and retry later rather than dropping it.
 * See referral.routes.ts in apps/api for the source mapping.
 */
const DEFINITIVE_REJECTION_STATUSES = new Set([400, 403, 404, 409]);

/** True if `err` is a server-side rejection that will never succeed on retry. */
export function isDefinitiveReferralRejection(err: unknown): boolean {
  return err instanceof ApiError && DEFINITIVE_REJECTION_STATUSES.has(err.status);
}

export const referralApi = {
  /** Validate a referral code — public, no auth needed */
  async validate(
    code: string,
  ): Promise<{ valid: boolean; referrerName?: string }> {
    const res = await fetch(
      `/api/proxy/referrals/validate/${encodeURIComponent(code.toUpperCase())}`,
    );
    return res.json();
  },

  /** Apply a referral code to the current user */
  async apply(
    code: string,
    sourceType: "CODE" | "LINK" = "CODE",
  ): Promise<void> {
    await fetchApi("/referrals/apply", {
      method: "POST",
      body: JSON.stringify({ code: code.toUpperCase(), sourceType }),
    });
  },

  /** Get the current user's referral summary */
  async getMySummary(): Promise<ReferralSummary> {
    return fetchApi<ReferralSummary>("/referrals/me");
  },

  /** Get the current user's reward history */
  async getMyRewards(): Promise<ReferralReward[]> {
    return fetchApi<ReferralReward[]>("/referrals/me/rewards");
  },
};
